import { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Add, Remove } from '@mui/icons-material';
import {
  Box,
  BoxProps,
  Stack,
  Table as TableBase,
  TableBody as TableBodyBase,
  TableCell as TableCellBase,
  TableHead as TableHeadBase,
  TableRow,
  Collapse,
  Skeleton,
} from '@mui/material';
import type { StyledComponent } from 'styled-components';
import { ReactComponent as ArrowDownBase } from '../../assets/arrow-down.svg';
import { ReactComponent as ArrowUpBase } from '../../assets/arrow-up.svg';
import CheckBox from '../Checkbox';
import { Flex, FlexColumn } from '../Flex';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';
import { getValue } from '../../utils/common';
import type { Column, SortDirection } from './types';

type RowProps<T> = {
  rowData: T;
  columns: Array<Column<T>>;
  onRowClick?: (rowData: T) => void;
  expandedRowRenderer?: (rowData: T) => JSX.Element | null;
  expandCondition?: (rowData: T) => boolean;
  expandOnLoad?: boolean;
  selectionColumn?: boolean | ((rowData: T) => boolean);
  isSelected?: boolean;
  onSelectChange?: () => void;
  onLoaded?: () => void;
  animate?: boolean;
  isFocused?: boolean;
  centerRowCell?: boolean;
  columnTranslations?: Record<string, number>;
  isReordering?: boolean;
  isFirstInGroup?: boolean;
  compact?: boolean;
};

type SortButtonsProps = {
  columnId: string;
  sortBy?: string;
  sortDirection: SortDirection;
  onSort: Function;
};

export const TABLE_HEAD_HEIGHT = 40;
export const MIN_COLUMN_WIDTH = 90;

export const TABLE_ROW_HEIGHT = 48;
export const TABLE_ROW_HEIGHT_COMPACT = 32;
export const TABLE_ROW_ESTIMATE = 60;
export const TABLE_ROW_ESTIMATE_COMPACT = 40;
export const GROUP_HEADER_HEIGHT = 56;
export const GROUP_HEADER_HEIGHT_COMPACT = 40;
const ROW_VERTICAL_PADDING = 14;
const ROW_VERTICAL_PADDING_COMPACT = 6;

export const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  overflow-y: hidden;
`;

type TableWrapperProps = BoxProps & { $bottomScrollPadding?: number };

export const TableWrapper: StyledComponent<
  typeof Box,
  any,
  TableWrapperProps,
  never
> = styled(Box)<TableWrapperProps>`
  height: 100%;
  overflow: auto;

  ::-webkit-scrollbar-track {
    margin-top: ${TABLE_HEAD_HEIGHT}px;
    margin-bottom: ${({ $bottomScrollPadding }) => $bottomScrollPadding || 0}px;
  }
`;

export const EmptyStateWrapper = styled(Flex)`
  height: calc(100% - ${TABLE_HEAD_HEIGHT}px);
  width: 100%;
`;

export const LoadingWrapper = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

export const StyledTable = styled(TableBase)`
  &.MuiTable-root {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
  }
`;

export const TableHead = styled(TableHeadBase)<{ $disabled?: boolean }>`
  background: white;
  margin: 0;
  position: sticky;
  top: 0;
  z-index: 2;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

export const TableBody = TableBodyBase;

export const ColumnActions = styled(Flex)<{ $isActive?: boolean }>`
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  transition: opacity 0.15s ease;
  gap: 12px;
  flex-shrink: 0;
`;

export const DraggableHeaderCell = styled(TableCellBase)<{
  $isDragging?: boolean;
  $isDraggable?: boolean;
  $translateX?: number;
  $isReordering?: boolean;
  $customVariant?: string;
  $selectionColumn?: boolean;
}>`
  &.MuiTableCell-root {
    border: none !important;
    position: relative;
    padding-top: 0;
    padding-bottom: ${({ $selectionColumn }) =>
      $selectionColumn ? '2px' : '12px'};
    font-size: 13px;
    font-weight: normal;
    height: ${TABLE_HEAD_HEIGHT}px;
    white-space: nowrap;
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'default')};
    user-select: none;
    transition: ${({ $isReordering }) =>
      $isReordering ? 'transform 0.2s ease, opacity 0.15s ease' : 'none'};
    transform: translateX(${({ $translateX }) => $translateX || 0}px);

    &:hover ${ColumnActions} {
      opacity: 1;
    }

    ${({ $isDragging }) =>
      $isDragging &&
      `
      opacity: 0.4;
      cursor: grabbing;
      z-index: 10;
    `}
  }
`;

export const TableCell = styled(TableCellBase)<{
  $customVariant?: string;
  $selectionColumn?: boolean;
  $centerRowCell?: boolean;
  $translateX?: number;
  $isReordering?: boolean;
  $hideTopBorder?: boolean;
  $compact?: boolean;
}>`
  &.MuiTableCell-root {
    border: none;
    border-top: ${({ theme, $hideTopBorder }) =>
      $hideTopBorder ? 'none' : `1px solid ${theme.palette.common.gray}`};
    transition: ${({ $isReordering }) =>
      $isReordering ? 'transform 0.2s ease' : 'none'};
    transform: translateX(${({ $translateX }) => $translateX || 0}px);
    vertical-align: ${({ $customVariant, $centerRowCell }) =>
      $customVariant === 'head' || $centerRowCell ? 'inherit' : 'top'};
    padding-right: ${({ $selectionColumn }) =>
      $selectionColumn ? '34px' : '8px'};

    &:first-child {
      padding-left: ${({ $customVariant, $selectionColumn }) =>
        $selectionColumn
          ? $customVariant === 'head'
            ? '16.5px'
            : '7px'
          : 'auto'};
    }
    padding-top: ${({ $customVariant, $centerRowCell, $compact }) =>
      $customVariant === 'head' || $centerRowCell
        ? 0
        : $compact
        ? ROW_VERTICAL_PADDING_COMPACT
        : ROW_VERTICAL_PADDING}px;
    padding-bottom: ${({
      $customVariant,
      $centerRowCell,
      $selectionColumn,
      $compact,
    }) =>
      $customVariant === 'head' && $selectionColumn
        ? '12px !important'
        : $customVariant === 'head' || $centerRowCell
        ? '0px'
        : `${
            $compact ? ROW_VERTICAL_PADDING_COMPACT : ROW_VERTICAL_PADDING
          }px`};
    height: ${({ $customVariant, $compact }) =>
      $customVariant === 'head'
        ? TABLE_HEAD_HEIGHT
        : $compact
        ? TABLE_ROW_HEIGHT_COMPACT
        : TABLE_ROW_HEIGHT}px;
    white-space: nowrap;
    font-size: 14px;

    ${({ $customVariant, $selectionColumn }) =>
      $customVariant === 'head' &&
      `
        border: none !important;
        position: relative;
        padding-top: 0;
        padding-bottom: ${$selectionColumn ? '2px' : '12px'};
        font-size: 13px;
        font-weight: normal;
      `}

    ${({ $customVariant }) =>
      $customVariant === 'expandedContent' &&
      `
        border: none !important;
        height: 0;
        padding: 0 16px;
        > .MuiCollapse-root {
          margin-bottom: 4px;
        }
      `}
  }
`;

export const ArrowUp = styled(ArrowUpBase)<{ $active: boolean }>`
  cursor: pointer;
  path {
    fill: ${({ theme, $active }) =>
      $active ? theme.palette.common.blue : theme.palette.common.gray2};
  }
  &:hover {
    path {
      fill: ${({ theme, $active }) =>
        $active ? theme.palette.common.blue5 : theme.palette.common.black};
    }
  }
`;

export const ArrowDown = styled(ArrowDownBase)<{ $active: boolean }>`
  cursor: pointer;
  path {
    fill: ${({ theme, $active }) =>
      $active ? theme.palette.common.blue : theme.palette.common.gray2};
  }
  &:hover {
    path {
      fill: ${({ theme, $active }) =>
        $active ? theme.palette.common.blue5 : theme.palette.common.black};
    }
  }
`;

export const ClickableTableRow = styled(TableRow)<{
  $clickable?: boolean;
  $isSelected?: boolean;
  $animate?: boolean;
}>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected && theme.palette.common.gray10};
  &:hover {
    background-color: ${({ theme, $clickable }) =>
      $clickable ? theme.palette.common.gray3 : 'none'};
  }
  animation: ${({ $animate }) => $animate && 'blink 1000ms 4 ease-in-out'};

  @keyframes blink {
    0% {
      background-color: 'none';
    }
    50% {
      background-color: ${({ theme }) => theme.palette.common.gray16};
    }
    100% {
      background-color: 'none';
    }
  }
`;

export const ExpandButtonCell = styled(TableCell)`
  width: 40px;
  max-width: 40px;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  > svg {
    width: 18px;
    color: ${({ theme }) => theme.palette.common.gray2};
    margin-top: 14px;
    margin-left: -20px;
  }
`;

export const SkeletonRow = styled(Flex)`
  height: 48px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  border-top: ${({ theme }) => `1px solid ${theme.palette.common.gray}`};
  gap: 48px;
  overflow: hidden;
`;

export const SkeletonCell = styled(Skeleton)`
  border-radius: 4px;
  width: 163px;
  min-width: 163px;
  height: 20px;
`;

export const HeaderCellContent = styled(Flex)<{
  $centerContent?: boolean;
}>`
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: ${({ $centerContent }) =>
    $centerContent ? 'center' : 'auto'};
  height: 100%;
`;

export const Resizer = styled(Flex)<{ $isResizing: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 15px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 10;
  justify-content: center;

  &:after {
    content: '';
    height: 24px;
    width: 1px;
    background: ${({ theme }) => theme.palette.common.gray};
    transition: background 0.2s;
    border-radius: 4px;
    margin-top: 2px;
  }

  &:hover:after {
    background: ${({ theme }) => theme.palette.common.gray4};
    width: 2px;
  }

  ${({ $isResizing, theme }) =>
    $isResizing &&
    `
    &:after {
      background: ${theme.palette.common.gray4};
         width: 2px;
    }
  `}
`;

const isCellEmpty = <T,>(rowData: T, column: Column<T>) =>
  !column.metaColumn &&
  !column.showValueWhenEmpty &&
  !getValue(rowData, column.valueKey || column.id)
    ?.toString()
    .toLowerCase()
    .trim();

export const isRowSelectable = <T extends object>(
  selectionColumn: boolean | ((rowData: T) => boolean) | undefined,
  rowData: T,
) =>
  typeof selectionColumn === 'boolean'
    ? selectionColumn
    : selectionColumn?.(rowData);

export const Row = <T extends object>({
  rowData,
  columns,
  onRowClick,
  expandedRowRenderer,
  expandCondition,
  expandOnLoad,
  selectionColumn,
  isSelected,
  onSelectChange,
  onLoaded,
  animate,
  isFocused,
  centerRowCell,
  columnTranslations,
  isReordering,
  isFirstInGroup,
  compact,
}: RowProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(expandOnLoad);

  const isExpandEnabled = useMemo(
    () =>
      !!expandedRowRenderer && (!expandCondition || expandCondition?.(rowData)),
    [expandCondition, expandedRowRenderer, rowData],
  );

  const hasSelectionColumn = isRowSelectable(selectionColumn, rowData);

  useEffect(() => {
    onLoaded?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ClickableTableRow
        role="row"
        aria-label="table-row"
        $clickable={isExpandEnabled || hasSelectionColumn || !!onRowClick}
        onClick={() => {
          if (onRowClick) {
            onRowClick(rowData);
          } else if (isExpandEnabled) {
            setIsExpanded(!isExpanded);
          } else if (hasSelectionColumn) {
            onSelectChange?.();
          }
        }}
        $isSelected={isSelected || isFocused}
        $animate={animate}
      >
        {selectionColumn && (
          <TableCell
            $customVariant={isExpanded ? 'expandedLeft' : ''}
            $selectionColumn={hasSelectionColumn}
            $hideTopBorder={isFirstInGroup}
            $compact={compact}
            onClick={(e) => {
              e.stopPropagation();
              if (hasSelectionColumn) onSelectChange?.();
            }}
          >
            <CheckBox
              sx={{
                ml: '9px',
                visibility: hasSelectionColumn ? 'visible' : 'hidden',
              }}
              $noPadding
              checked={!!isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelectChange?.()}
            />
          </TableCell>
        )}

        {columns.map((c, i) => (
          <TableCell
            role="cell"
            aria-label={c.header}
            key={c.id}
            align={
              c.centerContent ? 'center' : c.metaColumn ? 'right' : undefined
            }
            $centerRowCell={centerRowCell}
            $translateX={columnTranslations?.[c.id] || 0}
            $isReordering={isReordering}
            $hideTopBorder={isFirstInGroup}
            $compact={compact}
            $customVariant={
              i === 0 && !selectionColumn && isExpanded ? 'expandedLeft' : ''
            }
          >
            {isCellEmpty(rowData, c) ? (
              '-'
            ) : c.renderer ? (
              c.renderer(rowData)
            ) : (
              <OverflowTooltip>
                {getValue(rowData, c.valueKey || c.id)?.toString()}
              </OverflowTooltip>
            )}
          </TableCell>
        ))}
        {!!expandedRowRenderer && (
          <ExpandButtonCell
            $customVariant={isExpanded ? 'expandedRight' : ''}
            $hideTopBorder={isFirstInGroup}
            $compact={compact}
          >
            {isExpanded ? (
              <Remove
                sx={{
                  visibility: isExpandEnabled ? 'visible' : 'hidden',
                }}
              />
            ) : (
              <Add
                sx={{
                  visibility: isExpandEnabled ? 'visible' : 'hidden',
                }}
              />
            )}
          </ExpandButtonCell>
        )}
      </ClickableTableRow>
      <TableRow>
        <TableCell
          role="contentinfo"
          $customVariant="expandedContent"
          colSpan={
            expandedRowRenderer && selectionColumn
              ? columns.length + 2
              : expandedRowRenderer || selectionColumn
              ? columns.length + 1
              : columns.length
          }
        >
          <Collapse in={isExpanded} unmountOnExit sx={{ minWidth: '100%' }}>
            <Stack sx={{ p: 2 }}>{expandedRowRenderer?.(rowData)}</Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const SortButtons = ({
  columnId,
  sortBy,
  sortDirection,
  onSort,
}: SortButtonsProps) => (
  <Stack gap={0.6} justifyContent="center">
    <ArrowUp
      $active={sortBy === columnId && sortDirection === 'asc'}
      onClick={() => onSort(columnId, 'asc')}
    />
    <ArrowDown
      $active={sortBy === columnId && sortDirection === 'desc'}
      onClick={() => onSort(columnId, 'desc')}
    />
  </Stack>
);

export const TableLoadingState = ({ minWidth }: { minWidth: number }) => (
  <FlexColumn sx={{ minWidth: `${minWidth}px` }}>
    {Array.from({ length: 10 }).map((_, index) => (
      <SkeletonRow key={index}>
        {Array.from({ length: 20 }).map((_, cellIndex) => (
          <SkeletonCell key={cellIndex} variant="rectangular" />
        ))}
      </SkeletonRow>
    ))}
  </FlexColumn>
);
